import { useState, useEffect } from 'react';
import {
  X, Globe, Target, AlertCircle, Code, Shield, Check, AlertTriangle,
  RefreshCw, Sparkles, Layers, ExternalLink, Gauge
} from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '../api';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';

const LOADING_STATUSES = [
  "Bypassing cache and launching Crawler...",
  "Crawling subpages (About, Services, Contact, FAQ)...",
  "Analyzing page hierarchies and internal link graphs...",
  "Extracting technical tags, OpenGraph metadata, and files...",
  "Evaluating SEO, performance speeds, and SSL certificates...",
  "Analyzing branding consistency and accessibility parameters...",
  "Groq AI Engine is generating Digital Intelligence Scorecard...",
  "Structuring custom conversion-rate optimization suggestions...",
  "Finalizing executive B2B outreach proposals..."
];

export default function WebsiteIntelligenceModal({
  isOpen,
  onClose,
  intelligence,
  businessName,
  leadId,
  onRefresh
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [expandedSection, setExpandedSection] = useState('seo');

  useEffect(() => {
    let interval;
    if (isRefreshing) {
      interval = setInterval(() => {
        setLoadingTextIndex((prev) => (prev + 1) % LOADING_STATUSES.length);
      }, 2500);
    } else {
      setLoadingTextIndex(0);
    }
    return () => clearInterval(interval);
  }, [isRefreshing]);

  if (!isOpen) return null;

  const hasNewEngine = !!(intelligence?.seo_audit && intelligence?.performance_audit);

  const handleRefresh = async () => {
    if (!leadId) return;
    setIsRefreshing(true);
    try {
      await api.post(`/leads/${leadId}/analyze-website?force=true`);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Refresh failed", err);
      alert("Failed to re-analyze website. Please check Groq connection or URL path.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const chartData = hasNewEngine && intelligence ? [
    { subject: 'SEO', score: intelligence.seo_audit?.score || 0 },
    { subject: 'Speed', score: intelligence.performance_audit?.score || 0 },
    { subject: 'Trust', score: intelligence.trust_analysis?.score || 0 },
    { subject: 'Conversion', score: intelligence.conversion_audit?.score || 0 },
    { subject: 'Branding', score: intelligence.branding_analysis?.score || 0 },
  ] : [];

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-400";
    if (score >= 50) return "text-orange-400";
    return "text-red-400";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-green-500/10 text-green-400 border border-green-500/20";
    if (score >= 50) return "bg-orange-500/10 text-orange-400 border border-orange-500/20";
    return "bg-red-500/10 text-red-400 border border-red-500/20";
  };

  const renderProgressRing = (score, radius = 28, strokeWidth = 4) => {
    const normalizedRadius = radius - strokeWidth * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (score / 100) * circumference;

    let ringColor = "text-green-500";
    if (score < 50) ringColor = "text-red-500";
    else if (score < 80) ringColor = "text-orange-500";

    return (
      <div className="relative flex items-center justify-center">
        <svg height={radius * 2} width={radius * 2} className="transform -rotate-90">
          <circle stroke="rgba(255, 255, 255, 0.08)" fill="transparent" strokeWidth={strokeWidth} r={normalizedRadius} cx={radius} cy={radius} />
          <circle
            className={cn("transition-all duration-1000 ease-out", ringColor)}
            stroke="currentColor" fill="transparent" strokeWidth={strokeWidth}
            strokeDasharray={circumference + ' ' + circumference}
            style={{ strokeDashoffset }}
            r={normalizedRadius} cx={radius} cy={radius}
          />
        </svg>
        <span className="absolute text-xs font-black text-white">{score}</span>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4 overflow-y-auto">
      {isRefreshing && (
        <div className="absolute inset-0 bg-slate-950/95 z-50 flex flex-col items-center justify-center p-6 text-center">
          <div className="relative w-24 h-24 mb-8">
            <div className="absolute inset-0 border-4 border-indigo-500/20 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            <div className="absolute inset-4 bg-indigo-500/10 rounded-full flex items-center justify-center animate-pulse">
              <Sparkles className="text-indigo-400" size={28} />
            </div>
          </div>
          <h3 className="text-2xl font-bold tracking-tight text-white mb-2">Analyzing Digital Footprint</h3>
          <p className="text-indigo-400/80 text-sm animate-pulse max-w-md h-8 transition-all">
            {LOADING_STATUSES[loadingTextIndex]}
          </p>
          <div className="w-64 h-1 bg-white/[0.06] rounded-full overflow-hidden mt-6">
            <div className="h-full bg-indigo-500 animate-[shimmer_2s_infinite]" style={{
              backgroundImage: 'linear-gradient(90deg, transparent, #818cf8, transparent)',
              backgroundSize: '200% 100%'
            }}></div>
          </div>
        </div>
      )}

      <div className="glass-card w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl border border-white/[0.08] shadow-2xl flex flex-col bg-slate-900/90 text-slate-100">

        <div className="p-6 border-b border-white/[0.06] flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-slate-900/40 backdrop-blur-md">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="p-2 bg-indigo-500/10 text-indigo-400 rounded-xl border border-indigo-500/20">
                <Sparkles size={20} />
              </span>
              <div>
                <h2 className="text-xl font-bold tracking-tight flex items-center gap-2 text-white">
                  AI Digital Intelligence Engine
                </h2>
                <p className="text-xs text-slate-400 font-mono flex items-center gap-1">
                  <Globe size={12} className="text-indigo-400" />
                  {businessName}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 self-stretch md:self-auto justify-end">
            {leadId && (
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold rounded-xl transition-all border border-indigo-400/20 shadow-lg shadow-indigo-600/15 disabled:opacity-50"
              >
                <RefreshCw size={14} className={cn(isRefreshing && "animate-spin")} />
                Re-analyze
              </button>
            )}
            <button onClick={onClose} className="p-2 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.06] rounded-xl transition-colors text-slate-400 hover:text-white">
              <X size={18} />
            </button>
          </div>
        </div>

        {!intelligence ? (
          <div className="flex-1 flex flex-col items-center justify-center py-20 text-slate-400">
            <Globe size={48} className="text-slate-600 mb-4 animate-pulse" />
            <p className="text-sm">No analysis has been generated for this lead yet.</p>
            {leadId && (
              <button onClick={handleRefresh} className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold flex items-center gap-2">
                <RefreshCw size={14} /> Start Analysis
              </button>
            )}
          </div>
        ) : !hasNewEngine ? (
          <div className="p-6 space-y-6 flex-1 overflow-y-auto">
            <div className="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-2xl flex items-start gap-3">
              <Sparkles className="text-indigo-400 shrink-0 mt-0.5" size={18} />
              <div>
                <h4 className="text-sm font-semibold text-white">Upgrade available for this audit!</h4>
                <p className="text-xs text-slate-400 mt-1">
                  This lead was scanned with an older intelligence model. Click <strong>Re-analyze</strong> in the top bar to unlock deep SEO, Speed, trust indicators, branding scores, and B2B ROI outreach recommendations.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white/[0.03] p-4 rounded-2xl border border-white/[0.06]">
                <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2 mb-2">
                  <Target size={16} className="text-blue-400" /> Value Proposition
                </h3>
                <p className="text-sm text-slate-400">{intelligence.value_proposition || 'Not found'}</p>
              </div>
              <div className="bg-white/[0.03] p-4 rounded-2xl border border-white/[0.06]">
                <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2 mb-2">
                  <Target size={16} className="text-purple-400" /> Target Audience
                </h3>
                <p className="text-sm text-slate-400">{intelligence.target_audience || 'Not found'}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-red-500/5 p-4 rounded-2xl border border-red-500/10">
                <h3 className="text-sm font-semibold text-red-400 flex items-center gap-2 mb-3">
                  <AlertCircle size={16} /> Missing Key Pages
                </h3>
                <div className="flex flex-wrap gap-2">
                  {intelligence.missing_pages?.length > 0 ? (
                    intelligence.missing_pages.map((p, i) => (
                      <span key={i} className="bg-red-500/10 text-red-400 text-xs px-2.5 py-1 rounded-lg border border-red-500/20 font-medium">{p}</span>
                    ))
                  ) : <span className="text-xs text-slate-500">None detected</span>}
                </div>
              </div>

              <div className="bg-orange-500/5 p-4 rounded-2xl border border-orange-500/10">
                <h3 className="text-sm font-semibold text-orange-400 flex items-center gap-2 mb-3">
                  <AlertTriangle size={16} /> SEO Issues
                </h3>
                <div className="flex flex-wrap gap-2">
                  {intelligence.seo_issues?.length > 0 ? (
                    intelligence.seo_issues.map((issue, i) => (
                      <span key={i} className="bg-orange-500/10 text-orange-400 text-xs px-2.5 py-1 rounded-lg border border-orange-500/20 font-medium">{issue}</span>
                    ))
                  ) : <span className="text-xs text-slate-500">None detected</span>}
                </div>
              </div>
            </div>

            <div className="bg-white/[0.03] p-4 rounded-2xl border border-white/[0.06]">
              <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2 mb-3">
                <Code size={16} className="text-green-400" /> Detected Technologies
              </h3>
              <div className="flex flex-wrap gap-2">
                {intelligence.tech_stack?.length > 0 ? (
                  intelligence.tech_stack.map((tech, i) => (
                    <span key={i} className="bg-white/[0.06] text-slate-300 text-xs px-2.5 py-1 rounded-lg border border-white/[0.06]">{tech}</span>
                  ))
                ) : <span className="text-xs text-slate-500">None detected</span>}
              </div>
            </div>

            <div className="text-right text-[10px] text-slate-500">
              Analyzed at: {new Date(intelligence.analyzed_at).toLocaleString()}
            </div>
          </div>
        ) : (
          <>
            <div className="px-6 border-b border-white/[0.06] flex gap-4 overflow-x-auto bg-slate-900/10">
              {['overview', 'audits', 'recommendations', 'social'].map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={cn("py-3 text-xs font-semibold border-b-2 transition-all shrink-0 capitalize",
                    activeTab === tab ? "border-indigo-500 text-white font-bold" : "border-transparent text-slate-400 hover:text-slate-200")}>
                  {tab === 'social' ? 'Social & Tech' : tab}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">

              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-4 flex flex-col justify-between">
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-1.5">
                          <Gauge size={14} className="text-indigo-400" /> Category Scorecard
                        </h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.06] flex items-center justify-between">
                            <span className="text-xs text-slate-400 font-medium">SEO</span>
                            {renderProgressRing(intelligence.seo_audit?.score || 0)}
                          </div>
                          <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.06] flex items-center justify-between">
                            <span className="text-xs text-slate-400 font-medium">Speed</span>
                            {renderProgressRing(intelligence.performance_audit?.score || 0)}
                          </div>
                          <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.06] flex items-center justify-between">
                            <span className="text-xs text-slate-400 font-medium">Trust</span>
                            {renderProgressRing(intelligence.trust_analysis?.score || 0)}
                          </div>
                          <div className="bg-white/[0.02] p-3 rounded-xl border border-white/[0.06] flex items-center justify-between">
                            <span className="text-xs text-slate-400 font-medium">Conversion</span>
                            {renderProgressRing(intelligence.conversion_audit?.score || 0)}
                          </div>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-white/[0.06] flex items-center justify-between bg-white/[0.02] p-3 rounded-xl mt-3">
                        <div>
                          <h5 className="text-xs font-semibold text-slate-300">Branding Score</h5>
                          <p className="text-[10px] text-slate-500">Design consistency</p>
                        </div>
                        <span className={cn("text-lg font-black", getScoreColor(intelligence.branding_analysis?.score || 0))}>
                          {intelligence.branding_analysis?.score}%
                        </span>
                      </div>
                    </div>

                    <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] flex flex-col items-center justify-center">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 self-start mb-2 flex items-center gap-1.5">
                        <Layers size={14} className="text-indigo-400" /> Audit Footprint
                      </h4>
                      <div className="w-full h-[180px] flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={chartData}>
                            <PolarGrid stroke="rgba(255,255,255,0.06)" />
                            <PolarAngleAxis dataKey="subject" stroke="rgba(255,255,255,0.4)" fontSize={9} />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="rgba(255,255,255,0.15)" fontSize={8} />
                            <Radar name="Score" dataKey="score" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-4 flex flex-col">
                      <div>
                        <div className="flex justify-between items-start">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                            <Sparkles size={14} className="text-indigo-400" /> Opportunity Score
                          </h4>
                          <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider", getScoreBg(intelligence.opportunity_score?.total || 0))}>
                            {intelligence.opportunity_score?.total && intelligence.opportunity_score.total >= 60 ? "High Outreach Potential" : "Low Outreach Potential"}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1 mt-3">
                          <span className="text-4xl font-extrabold text-white">{intelligence.opportunity_score?.total}</span>
                          <span className="text-xs text-slate-500 font-semibold">/ 100</span>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">Higher score represents more gaps (better pitch angles).</p>
                      </div>

                      <div className="flex-1 overflow-y-auto max-h-[110px] space-y-1.5 pr-1 text-[11px]">
                        {intelligence.opportunity_score?.breakdown?.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center py-1 border-b border-white/[0.03]">
                            <span className="text-slate-400 font-medium truncate max-w-[150px]">{item.reason}</span>
                            <span className="font-bold text-indigo-400">+{item.points}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {intelligence.executive_summary && (
                    <div className="p-5 bg-white/[0.02] border border-white/[0.06] rounded-2xl space-y-4">
                      <div>
                        <h4 className="text-sm font-bold text-white flex items-center gap-2">
                          <AlertCircle size={16} className="text-indigo-400" /> Executive Audit Summary
                        </h4>
                        <p className="text-xs text-slate-400 mt-2 leading-relaxed">{intelligence.executive_summary.business_overview}</p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4 pt-2">
                        <div className="space-y-1.5">
                          <h5 className="text-[11px] font-bold uppercase text-green-400 tracking-wide">Key Strengths</h5>
                          <ul className="space-y-1">
                            {intelligence.executive_summary.strengths.slice(0, 3).map((item, i) => (
                              <li key={i} className="text-xs text-slate-400 flex items-center gap-1.5">
                                <Check size={12} className="text-green-500 shrink-0" /> <span className="truncate">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="space-y-1.5">
                          <h5 className="text-[11px] font-bold uppercase text-red-400 tracking-wide">Primary Weaknesses</h5>
                          <ul className="space-y-1">
                            {intelligence.executive_summary.weaknesses.slice(0, 3).map((item, i) => (
                              <li key={i} className="text-xs text-slate-400 flex items-center gap-1.5">
                                <AlertTriangle size={12} className="text-red-500 shrink-0" /> <span className="truncate">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 pt-3 border-t border-white/[0.06]">
                        <div className="bg-slate-900/50 p-3 rounded-xl border border-white/[0.06]">
                          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Estimated ROI</span>
                          <span className="text-xs font-black text-green-400 block mt-1">{intelligence.executive_summary.estimated_roi}</span>
                        </div>
                        <div className="bg-slate-900/50 p-3 rounded-xl border border-white/[0.06]">
                          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Quick Win</span>
                          <span className="text-xs font-bold text-slate-300 block truncate mt-1">{intelligence.executive_summary.quick_wins[0] || "Optimize Metadata"}</span>
                        </div>
                        <div className="bg-slate-900/50 p-3 rounded-xl border border-white/[0.06]">
                          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Priority Fix</span>
                          <span className="text-xs font-bold text-slate-300 block truncate mt-1">{intelligence.executive_summary.priority_fixes[0] || "Install Booking Form"}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-900/30 border border-white/[0.06] rounded-xl">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Value Proposition</span>
                      <p className="text-xs text-slate-300 font-medium mt-1 leading-relaxed">{intelligence.value_proposition || "Not specified."}</p>
                    </div>
                    <div className="p-4 bg-slate-900/30 border border-white/[0.06] rounded-xl">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Target Audience</span>
                      <p className="text-xs text-slate-300 font-medium mt-1 leading-relaxed">{intelligence.target_audience || "Not specified."}</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'audits' && (
                <div className="space-y-4">
                  {/* SEO Audit Accordion */}
                  <div className="border border-white/[0.06] rounded-2xl overflow-hidden">
                    <button onClick={() => setExpandedSection(expandedSection === 'seo' ? null : 'seo')}
                      className="w-full p-4 bg-slate-900/50 hover:bg-slate-900/80 flex items-center justify-between transition-colors">
                      <div className="flex items-center gap-2">
                        <span className={cn("px-2 py-1 text-xs font-black rounded-lg", getScoreBg(intelligence.seo_audit?.score || 0))}>{intelligence.seo_audit?.score}%</span>
                        <span className="font-bold text-sm text-white">Search Engine Optimization (SEO)</span>
                      </div>
                      <span className="text-xs text-slate-500 font-bold">{expandedSection === 'seo' ? 'Collapse' : 'Expand'}</span>
                    </button>

                    {expandedSection === 'seo' && intelligence.seo_audit && (
                      <div className="p-4 bg-slate-900/20 border-t border-white/[0.06] space-y-4 text-xs">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-slate-950/20 p-3 rounded-xl border border-white/[0.06] space-y-2">
                            <div className="text-[10px] font-bold uppercase text-slate-500">Title Tag</div>
                            <div className="text-slate-300 font-semibold">{intelligence.seo_audit.title_tag || "Missing"}</div>
                          </div>
                          <div className="bg-slate-950/20 p-3 rounded-xl border border-white/[0.06] space-y-2">
                            <div className="text-[10px] font-bold uppercase text-slate-500">Meta Description</div>
                            <div className="text-slate-300 font-semibold">{intelligence.seo_audit.meta_description || "Missing"}</div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                          <div className="bg-slate-950/10 p-2.5 rounded-xl border border-white/[0.06]">
                            <span className="text-lg font-black text-white">{intelligence.seo_audit.internal_links}</span>
                            <span className="text-[10px] text-slate-500 block">Internal Links</span>
                          </div>
                          <div className="bg-slate-950/10 p-2.5 rounded-xl border border-white/[0.06]">
                            <span className="text-lg font-black text-white">{intelligence.seo_audit.external_links}</span>
                            <span className="text-[10px] text-slate-500 block">External Links</span>
                          </div>
                          <div className="bg-slate-950/10 p-2.5 rounded-xl border border-white/[0.06]">
                            <span className={cn("text-lg font-black", intelligence.seo_audit.broken_links > 0 ? "text-red-400" : "text-green-400")}>
                              {intelligence.seo_audit.broken_links}
                            </span>
                            <span className="text-[10px] text-slate-500 block">Broken Links</span>
                          </div>
                          <div className="bg-slate-950/10 p-2.5 rounded-xl border border-white/[0.06]">
                            <span className="text-lg font-black text-white">
                              {intelligence.seo_audit.missing_image_alt_tags ? "Gaps" : "Pass"}
                            </span>
                            <span className="text-[10px] text-slate-500 block">Image ALT tags</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.seo_audit.has_canonical ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Canonical Tag</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.seo_audit.has_robots ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Robots.txt Meta</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.seo_audit.has_sitemap ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">XML Sitemap</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.seo_audit.has_structured_data ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Structured Schema</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.seo_audit.has_opengraph ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">OpenGraph Cards</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.seo_audit.has_twitter_cards ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Twitter Card Tags</span>
                          </div>
                        </div>

                        {(intelligence.seo_audit.missing_h1 || intelligence.seo_audit.duplicate_headings) && (
                          <div className="p-3 bg-red-500/5 border border-red-500/10 rounded-xl text-red-400 flex items-start gap-2">
                            <AlertCircle size={14} className="shrink-0 mt-0.5" />
                            <div>
                              {intelligence.seo_audit.missing_h1 && <div className="font-semibold">Missing primary H1 tag on homepage!</div>}
                              {intelligence.seo_audit.duplicate_headings && <div className="text-[11px] mt-0.5">Detected duplicate heading texts. Structure headers sequentially (H1 &gt; H2 &gt; H3).</div>}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Performance Audit Accordion */}
                  <div className="border border-white/[0.06] rounded-2xl overflow-hidden">
                    <button onClick={() => setExpandedSection(expandedSection === 'perf' ? null : 'perf')}
                      className="w-full p-4 bg-slate-900/50 hover:bg-slate-900/80 flex items-center justify-between transition-colors">
                      <div className="flex items-center gap-2">
                        <span className={cn("px-2 py-1 text-xs font-black rounded-lg", getScoreBg(intelligence.performance_audit?.score || 0))}>{intelligence.performance_audit?.score}%</span>
                        <span className="font-bold text-sm text-white">Speed & Performance Audit</span>
                      </div>
                      <span className="text-xs text-slate-500 font-bold">{expandedSection === 'perf' ? 'Collapse' : 'Expand'}</span>
                    </button>

                    {expandedSection === 'perf' && intelligence.performance_audit && (
                      <div className="p-4 bg-slate-900/20 border-t border-white/[0.06] space-y-4 text-xs">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                          <div className="bg-slate-950/10 p-2.5 rounded-xl border border-white/[0.06]">
                            <span className="text-lg font-black text-white">{intelligence.performance_audit.estimated_page_size_kb} KB</span>
                            <span className="text-[10px] text-slate-500 block">Total Crawl Weight</span>
                          </div>
                          <div className="bg-slate-950/10 p-2.5 rounded-xl border border-white/[0.06]">
                            <span className="text-lg font-black text-white">{intelligence.performance_audit.images_count}</span>
                            <span className="text-[10px] text-slate-500 block">Total Images</span>
                          </div>
                          <div className="bg-slate-950/10 p-2.5 rounded-xl border border-white/[0.06]">
                            <span className="text-lg font-black text-white">{intelligence.performance_audit.js_files_count}</span>
                            <span className="text-[10px] text-slate-500 block">JS Resources</span>
                          </div>
                          <div className="bg-slate-950/10 p-2.5 rounded-xl border border-white/[0.06]">
                            <span className="text-lg font-black text-white">{intelligence.performance_audit.css_files_count}</span>
                            <span className="text-[10px] text-slate-500 block">CSS Stylesheets</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.performance_audit.has_lazy_loading ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-slate-500" />}
                            <span className="text-slate-400">Lazy Loading Images</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.performance_audit.has_compression ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Gzip/Brotli Compression</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.performance_audit.has_caching ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Cache-Control Active</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.performance_audit.is_mobile_friendly ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Mobile Responsive Tag</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.performance_audit.has_https ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">HTTPS Protocol</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Trust & Verification Accordion */}
                  <div className="border border-white/[0.06] rounded-2xl overflow-hidden">
                    <button onClick={() => setExpandedSection(expandedSection === 'trust' ? null : 'trust')}
                      className="w-full p-4 bg-slate-900/50 hover:bg-slate-900/80 flex items-center justify-between transition-colors">
                      <div className="flex items-center gap-2">
                        <span className={cn("px-2 py-1 text-xs font-black rounded-lg", getScoreBg(intelligence.trust_analysis?.score || 0))}>{intelligence.trust_analysis?.score}%</span>
                        <span className="font-bold text-sm text-white">Trust & Security Analysis</span>
                      </div>
                      <span className="text-xs text-slate-500 font-bold">{expandedSection === 'trust' ? 'Collapse' : 'Expand'}</span>
                    </button>

                    {expandedSection === 'trust' && intelligence.trust_analysis && (
                      <div className="p-4 bg-slate-900/20 border-t border-white/[0.06] space-y-3 text-xs">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.trust_analysis.has_testimonials ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Testimonials block</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.trust_analysis.has_reviews ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Customer reviews</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.trust_analysis.has_google_reviews ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-slate-500" />}
                            <span className="text-slate-400">Google Business review</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.trust_analysis.has_privacy_policy ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Privacy Policy link</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.trust_analysis.has_terms ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Terms of service</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.trust_analysis.has_ssl ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">SSL Certificate Active</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.trust_analysis.has_business_address ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-slate-500" />}
                            <span className="text-slate-400">Business Address text</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.trust_analysis.has_phone ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Phone Call links</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.trust_analysis.has_email ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Email/Mailto links</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.trust_analysis.has_social_icons ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-slate-500" />}
                            <span className="text-slate-400">Social Media Icons</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.trust_analysis.has_certifications ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-slate-500" />}
                            <span className="text-slate-400">Professional awards</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Conversion Audit Accordion */}
                  <div className="border border-white/[0.06] rounded-2xl overflow-hidden">
                    <button onClick={() => setExpandedSection(expandedSection === 'conv' ? null : 'conv')}
                      className="w-full p-4 bg-slate-900/50 hover:bg-slate-900/80 flex items-center justify-between transition-colors">
                      <div className="flex items-center gap-2">
                        <span className={cn("px-2 py-1 text-xs font-black rounded-lg", getScoreBg(intelligence.conversion_audit?.score || 0))}>{intelligence.conversion_audit?.score}%</span>
                        <span className="font-bold text-sm text-white">Conversion & Lead Capture Audit</span>
                      </div>
                      <span className="text-xs text-slate-500 font-bold">{expandedSection === 'conv' ? 'Collapse' : 'Expand'}</span>
                    </button>

                    {expandedSection === 'conv' && intelligence.conversion_audit && (
                      <div className="p-4 bg-slate-900/20 border-t border-white/[0.06] space-y-3 text-xs">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.conversion_audit.has_call_to_action ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">CTA (Call-to-action)</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.conversion_audit.has_forms ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Interactive Web Forms</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.conversion_audit.has_book_appointment ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Appointment Booker</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.conversion_audit.has_book_demo ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-slate-500" />}
                            <span className="text-slate-400">Book Demo CTA</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.conversion_audit.has_contact_button ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-red-500" />}
                            <span className="text-slate-400">Contact Us Link</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.conversion_audit.has_whatsapp ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-slate-500" />}
                            <span className="text-slate-400">WhatsApp Widget</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.conversion_audit.has_live_chat ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-slate-500" />}
                            <span className="text-slate-400">Live Chat Integrations</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.conversion_audit.has_newsletter ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-slate-500" />}
                            <span className="text-slate-400">Newsletter Form</span>
                          </div>
                          <div className="flex items-center gap-2 bg-slate-900/30 p-2.5 rounded-xl">
                            {intelligence.conversion_audit.has_lead_magnets ? <Check size={14} className="text-green-500" /> : <AlertTriangle size={14} className="text-slate-500" />}
                            <span className="text-slate-400">Lead Magnets (Guides)</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'recommendations' && (
                <div className="space-y-4">
                  <div className="p-4 bg-indigo-500/5 border border-indigo-500/10 rounded-2xl flex items-center gap-3">
                    <Sparkles className="text-indigo-400 shrink-0" size={18} />
                    <div className="text-xs text-slate-300 leading-normal">
                      The engine analyzed this website footprint and determined key improvements, ordered by expected business conversion impact.
                    </div>
                  </div>

                  <div className="space-y-2">
                    {intelligence.recommendations && intelligence.recommendations.length > 0 ? (
                      intelligence.recommendations.map((rec, i) => (
                        <div key={i} className="flex gap-4 p-4 bg-white/[0.02] border border-white/[0.06] rounded-2xl hover:bg-white/[0.03] transition-colors items-center">
                          <div className="w-8 h-8 rounded-full bg-slate-900 border border-white/[0.08] flex items-center justify-center font-mono font-bold text-xs shrink-0 text-slate-300">{i + 1}</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold text-white leading-snug">{rec.improvement}</div>
                            <div className="flex items-center gap-2 mt-2">
                              <span className={cn("px-2 py-0.5 rounded text-[9px] font-bold uppercase",
                                rec.impact === "High" ? "bg-red-500/10 text-red-400 border border-red-500/10" :
                                  rec.impact === "Medium" ? "bg-orange-500/10 text-orange-400 border border-orange-500/10" :
                                    "bg-blue-500/10 text-blue-400 border border-blue-500/10")}>
                                {rec.impact} Impact
                              </span>
                              <span className="text-[10px] text-slate-500 font-mono font-semibold flex items-center gap-0.5">
                                {rec.impact === "High" ? "★★★★★" : rec.impact === "Medium" ? "★★★☆☆" : "★☆☆☆☆"}
                              </span>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <span className="bg-green-500/10 text-green-400 border border-green-500/20 text-[10px] font-extrabold px-2.5 py-1 rounded-lg">{rec.estimated_increase}</span>
                          </div>
                        </div>
                      ))
                    ) : <div className="text-center py-10 text-slate-500 text-xs">No AI recommendations generated.</div>}
                  </div>
                </div>
              )}

              {activeTab === 'social' && (
                <div className="space-y-6">
                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                      <Shield size={14} className="text-indigo-400" /> Social Media Channels
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {intelligence.social_presence && Object.entries(intelligence.social_presence).map(([platform, value]) => {
                        if (platform === 'missing_platforms') return null;
                        const isLinkActive = !!value;
                        return (
                          <div key={platform} className={cn("p-3 rounded-xl border flex flex-col justify-between h-20 transition-all",
                            isLinkActive ? "bg-indigo-500/5 border-indigo-500/20 hover:bg-indigo-500/10" : "bg-white/[0.02] border-white/[0.06] opacity-50")}>
                            <div className="text-[10px] font-bold uppercase text-slate-500">{platform}</div>
                            {isLinkActive ? (
                              <a href={value} target="_blank" rel="noreferrer" className="text-xs font-bold text-white flex items-center gap-1 hover:underline">
                                View Profile <ExternalLink size={10} className="text-indigo-400" />
                              </a>
                            ) : <span className="text-xs font-semibold text-slate-600">Missing</span>}
                          </div>
                        );
                      })}
                    </div>

                    {(intelligence.social_presence?.missing_platforms?.length ?? 0) > 0 && (
                      <div className="pt-2">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-2">Recommended Platforms to Add</span>
                        <div className="flex flex-wrap gap-2">
                          {intelligence.social_presence?.missing_platforms?.map((p, idx) => (
                            <span key={idx} className="bg-slate-900 text-slate-400 border border-white/[0.06] text-[10px] px-2 py-0.5 rounded-lg">{p}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-slate-900/50 p-5 rounded-2xl border border-white/[0.06] space-y-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                      <Code size={14} className="text-indigo-400" /> Technographics & Tech Stack
                    </h4>
                    <div className="flex flex-wrap gap-2 pt-2">
                      {intelligence.tech_stack?.length > 0 ? (
                        intelligence.tech_stack.map((tech, i) => (
                          <span key={i} className="bg-white/[0.04] border border-white/[0.06] text-slate-300 text-xs px-2.5 py-1 rounded-lg">{tech}</span>
                        ))
                      ) : <span className="text-xs text-slate-500">No tools detected.</span>}
                    </div>
                  </div>
                </div>
              )}

            </div>

            <div className="p-4 border-t border-white/[0.06] bg-slate-900/20 text-[10px] text-slate-500 flex justify-between items-center px-6">
              <span>Security verified via SSL</span>
              <span>Analyzed on: {new Date(intelligence.analyzed_at).toLocaleString()}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
